if (!customElements.get('buyers-guide')) {
  customElements.define(
    'buyers-guide',
    class BuyersGuide extends HTMLElement {
      constructor() {
        super();
        this.state = {
          vehicle: '',
          productType: '',
          plugType: '',
          step: 1
        };
        this.scrollLockState = null;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.stepButtons = Array.from(this.querySelectorAll('[data-guide-step-button]'));
        this.topNavButtons = Array.from(this.querySelectorAll('[data-guide-nav-step]'));
        this.panels = Array.from(this.querySelectorAll('[data-guide-panel]'));
        this.backButtons = Array.from(this.querySelectorAll('[data-guide-back]'));
        this.progressTrack = this.querySelector('.buyers-guide__progress-track');
        this.progressFill = this.querySelector('[data-guide-progress-fill]');
        this.progressCurrent = this.querySelector('[data-guide-progress-current]');
        this.progressTotal = this.querySelector('[data-guide-progress-total]');
        this.totalSteps = this.stepButtons.length || 4;
        this.skipPlugTypeKeys = this.parseKeys(this.dataset.skipPlugKeys || 'adapter,extension_cord');
        this.skipPlugTypeLabels = this.parsePhrases(
          this.dataset.skipPlugLabels ||
            'adapter,adapters,适配器,適配器,adaptador,adaptadores,adaptateur,adaptateurs,adattatore,adattatori,adaptere,adaptor,adaptor kit,adapter kit,アダプター,어댑터,extension,extension cord,extension cable,延长线,延長線'
        );

        this.vehicleOptions = Array.from(this.querySelectorAll('[data-guide-select="vehicle"]'));
        this.productTypeOptions = Array.from(this.querySelectorAll('[data-guide-select="product_type"]'));
        this.plugTypeOptions = Array.from(this.querySelectorAll('[data-guide-select="plug_type"]'));

        this.emptyOptionMessages = {
          2: this.querySelector('[data-guide-empty-options="2"]'),
          3: this.querySelector('[data-guide-empty-options="3"]')
        };
        this.productItems = Array.from(this.querySelectorAll('[data-guide-product]'));
        this.emptyProductsMessage = this.querySelector('[data-guide-empty-products]');
        this.demoProducts = this.querySelector('[data-guide-demo-products]');
        this.realProductsContainer = this.querySelector(
          '[data-guide-panel="4"] .buyers-guide__products--final:not([data-guide-demo-products])'
        );

        this.summaryTargets = {
          vehicle: this.querySelector('[data-guide-selected-label="vehicle"]'),
          productType: this.querySelector('[data-guide-selected-label="product_type"]'),
          plugType: this.querySelector('[data-guide-selected-label="plug_type"]')
        };
        this.specModal = this.querySelector('[data-guide-spec-modal]');
        this.specTargets = {
          connectorType: this.querySelector('[data-guide-spec-target="connector_type"]'),
          acInputPlug: this.querySelector('[data-guide-spec-target="ac_input_plug"]'),
          operatingFrequency: this.querySelector('[data-guide-spec-target="operating_frequency"]'),
          operatingVoltage: this.querySelector('[data-guide-spec-target="operating_voltage"]'),
          installationMethod: this.querySelector('[data-guide-spec-target="installation_method"]'),
          operatingCurrent: this.querySelector('[data-guide-spec-target="operating_current"]'),
          outputPower: this.querySelector('[data-guide-spec-target="output_power"]')
        };
        this.specProductTitle = this.querySelector('[data-guide-spec-product-title]');
        this.specProductImage = this.querySelector('[data-guide-spec-image]');
        this.specAddCartButton = this.querySelector('[data-guide-spec-add-cart]');
        this.specFallbackItems = Array.from(this.querySelectorAll('[data-guide-spec-fallback-item]'));
        this.specActiveFormId = '';
        this.specAddPending = false;
        this.specProductDefaults = {
          title: this.specProductTitle?.textContent?.trim() || '',
          imageSrc: this.specProductImage?.getAttribute('src') || '',
          imageAlt: this.specProductImage?.getAttribute('alt') || 'Product image'
        };
        this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
        this.onAjaxProductAdded = this.handleAjaxProductAdded.bind(this);
        this.onAjaxProductError = this.handleAjaxProductError.bind(this);

        if (this.progressTotal) {
          this.progressTotal.textContent = this.formatStepNumber(this.totalSteps);
        }

        document.addEventListener('keydown', this.onDocumentKeydown);
        document.addEventListener('ajaxProduct:added', this.onAjaxProductAdded);
        document.addEventListener('ajaxProduct:error', this.onAjaxProductError);
        this.addEventListener('click', this.onClick.bind(this));
        this.initializeVariantSelectors();
        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onDocumentKeydown);
        document.removeEventListener('ajaxProduct:added', this.onAjaxProductAdded);
        document.removeEventListener('ajaxProduct:error', this.onAjaxProductError);
        this.closeSpecsModal();
        this.unlockPageScroll();
      }

      formatStepNumber(step) {
        return String(Math.max(step, 0)).padStart(2, '0');
      }

      normalizeKey(value) {
        return String(value || '')
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      parseKeys(value) {
        if (!value) return [];
        return String(value)
          .split(',')
          .map((item) => this.normalizeKey(item))
          .filter(Boolean);
      }

      parsePhrases(value) {
        if (!value) return [];
        return String(value)
          .split(',')
          .map((item) => String(item).trim().toLowerCase())
          .filter(Boolean);
      }

      buildVariantUrl(baseUrl, variantId) {
        const normalizedBase = String(baseUrl || '').trim();
        if (!normalizedBase) return '';
        const normalizedVariantId = String(variantId || '').trim();
        if (!normalizedVariantId) return normalizedBase;

        try {
          const url = new URL(normalizedBase, window.location.origin);
          url.searchParams.set('variant', normalizedVariantId);
          if (/^https?:\/\//i.test(normalizedBase)) return url.toString();
          return `${url.pathname}${url.search}${url.hash}`;
        } catch (error) {
          const separator = normalizedBase.includes('?') ? '&' : '?';
          if (/([?&])variant=/.test(normalizedBase)) {
            return normalizedBase.replace(/([?&])variant=[^&#]*/i, `$1variant=${encodeURIComponent(normalizedVariantId)}`);
          }
          return `${normalizedBase}${separator}variant=${encodeURIComponent(normalizedVariantId)}`;
        }
      }

      initializeVariantSelectors() {
        this.variantSelectorsByCard = new WeakMap();

        this.productItems.forEach((card) => {
          const variantsScript = card.querySelector('[data-guide-variants-json]');
          const variantOptionButtons = Array.from(card.querySelectorAll('[data-guide-variant-option]'));
          if (!variantsScript || !variantOptionButtons.length) return;

          let variants = [];
          try {
            variants = JSON.parse(variantsScript.textContent || '[]');
          } catch (error) {
            variants = [];
          }
          if (!Array.isArray(variants) || !variants.length) return;

          const form = card.querySelector('form[id^="BuyersGuideProductForm-"]');
          const idInput = form?.querySelector('input[name="id"]');
          const primaryButton = card.querySelector('[data-guide-submit-form]');
          const hiddenSubmit = form?.querySelector('.buyers-guide__native-submit');
          const quickViewButton = card.querySelector('[data-guide-open-specs]');
          const priceNode = card.querySelector('.buyers-guide__final-price');
          const optionGroupEls = Array.from(card.querySelectorAll('[data-guide-option-group]'));
          const optionIndices = optionGroupEls
            .map((groupEl) => Number(groupEl.dataset.guideOptionGroup))
            .filter((index) => Number.isInteger(index) && index >= 0);

          const defaultVariantId = Number(idInput?.value || primaryButton?.dataset.variantId || '');
          let activeVariant = variants.find((variant) => Number(variant.id) === defaultVariantId);
          if (!activeVariant) {
            activeVariant = variants.find((variant) => variant.available) || variants[0];
          }
          if (!activeVariant) return;

          const selectedValues = {};
          if (Array.isArray(activeVariant.options)) {
            optionIndices.forEach((index) => {
              selectedValues[index] = String(activeVariant.options[index] || '');
            });
          }

          const state = {
            variants,
            optionIndices,
            selectedValues,
            idInput,
            primaryButton,
            hiddenSubmit,
            quickViewButton,
            priceNode
          };
          this.variantSelectorsByCard.set(card, state);
          this.applyVariantToCard(card, activeVariant, { syncOptionTags: true });
        });
      }

      variantMatchesSelection(variant, selectedValues, optionIndices) {
        for (const index of optionIndices) {
          const selectedValue = String(selectedValues[index] || '');
          if (!selectedValue) continue;
          const variantValue = String(variant.options?.[index] || '');
          if (variantValue !== selectedValue) return false;
        }
        return true;
      }

      findVariantForSelection(state, changedIndex) {
        if (!state?.variants?.length) return null;

        const exactAvailableMatch = state.variants.find((variant) =>
          variant.available && this.variantMatchesSelection(variant, state.selectedValues, state.optionIndices || [])
        );
        if (exactAvailableMatch) return exactAvailableMatch;

        const exactMatch = state.variants.find((variant) =>
          this.variantMatchesSelection(variant, state.selectedValues, state.optionIndices || [])
        );
        if (exactMatch) return exactMatch;
        return null;
      }

      updateVariantOptionAvailability(card) {
        const state = this.variantSelectorsByCard?.get(card);
        if (!state) return;

        const optionButtons = Array.from(card.querySelectorAll('[data-guide-variant-option]'));
        optionButtons.forEach((button) => {
          const optionIndex = Number(button.dataset.guideOptionIndex);
          const optionValue = String(button.dataset.guideOptionValue || '');
          if (Number.isNaN(optionIndex)) return;

          const hypotheticalSelection = { ...state.selectedValues };
          hypotheticalSelection[optionIndex] = optionValue;

          const hasAvailableVariant = state.variants.some(
            (variant) =>
              variant.available &&
              this.variantMatchesSelection(variant, hypotheticalSelection, state.optionIndices || [])
          );

          button.disabled = !hasAvailableVariant;
          button.setAttribute('aria-disabled', hasAvailableVariant ? 'false' : 'true');
          button.classList.toggle('is-unavailable', !hasAvailableVariant);
        });
      }

      applyVariantToCard(card, variant, { syncOptionTags = false } = {}) {
        const state = this.variantSelectorsByCard?.get(card);
        if (!state || !variant) return;

        const variantId = String(variant.id || '');

        if (state.idInput) {
          state.idInput.value = variantId;
        }

        if (state.hiddenSubmit) {
          state.hiddenSubmit.dataset.variantId = variantId;
        }

        if (state.primaryButton) {
          state.primaryButton.dataset.variantId = variantId;
        }

        const baseUrl = card.dataset.guideBaseUrl || card.dataset.guideCardUrl || '';
        if (baseUrl) {
          const nextCardUrl = this.buildVariantUrl(baseUrl, variantId);
          if (nextCardUrl) {
            card.dataset.guideCardUrl = nextCardUrl;
            const productLink = card.querySelector('[data-guide-product-link]');
            if (productLink) {
              productLink.setAttribute('href', nextCardUrl);
            }
          }
        }

        if (state.priceNode && variant.price) {
          state.priceNode.textContent = String(variant.price);
        }

        if (state.quickViewButton) {
          state.quickViewButton.dataset.guideSpecConnectorType = String(variant.specConnectorType || '');
          state.quickViewButton.dataset.guideSpecAcInputPlug = String(variant.specAcInputPlug || '');
          state.quickViewButton.dataset.guideSpecOperatingFrequency = String(variant.specOperatingFrequency || '');
          state.quickViewButton.dataset.guideSpecOperatingVoltage = String(variant.specOperatingVoltage || '');
          state.quickViewButton.dataset.guideSpecInstallationMethod = String(variant.specInstallationMethod || '');
          state.quickViewButton.dataset.guideSpecOperatingCurrent = String(variant.specOperatingCurrent || '');
          state.quickViewButton.dataset.guideSpecOutputPower = String(variant.specOutputPower || '');
        }

        if (syncOptionTags) {
          const optionButtons = Array.from(card.querySelectorAll('[data-guide-variant-option]'));
          optionButtons.forEach((button) => {
            const optionIndex = Number(button.dataset.guideOptionIndex);
            const optionValue = String(button.dataset.guideOptionValue || '');
            const isSelected = String(state.selectedValues[optionIndex] || '') === optionValue;
            button.classList.toggle('is-selected', isSelected);
            button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          });
        }

        this.updateVariantOptionAvailability(card);
      }

      selectVariantOption(button) {
        const card = button.closest('[data-guide-product]');
        if (!card || !this.variantSelectorsByCard) return;

        const state = this.variantSelectorsByCard.get(card);
        if (!state) return;

        const optionIndex = Number(button.dataset.guideOptionIndex);
        if (Number.isNaN(optionIndex) || optionIndex < 0) return;
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        const optionValue = String(button.dataset.guideOptionValue || '');
        const previousSelectedValues = { ...state.selectedValues };
        state.selectedValues[optionIndex] = optionValue;

        const selectedVariant = this.findVariantForSelection(state, optionIndex);
        if (!selectedVariant) {
          state.selectedValues = previousSelectedValues;
          this.updateVariantOptionAvailability(card);
          return;
        }

        if (Array.isArray(selectedVariant.options)) {
          const nextSelectedValues = {};
          (state.optionIndices || []).forEach((index) => {
            nextSelectedValues[index] = String(selectedVariant.options[index] || '');
          });
          state.selectedValues = nextSelectedValues;
        }

        this.applyVariantToCard(card, selectedVariant, { syncOptionTags: true });
      }

      lockPageScroll() {
        if (this.scrollLockState) return;
        const { documentElement, body } = document;
        if (!documentElement || !body) return;

        this.scrollLockState = {
          scrollY: window.scrollY || window.pageYOffset || 0,
          docOverflow: documentElement.style.overflow,
          bodyOverflow: body.style.overflow,
          bodyPosition: body.style.position,
          bodyTop: body.style.top,
          bodyWidth: body.style.width
        };

        documentElement.classList.add('buyers-guide-modal-open');
        body.classList.add('buyers-guide-modal-open');
        documentElement.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${this.scrollLockState.scrollY}px`;
        body.style.width = '100%';
      }

      unlockPageScroll() {
        if (!this.scrollLockState) return;
        const { documentElement, body } = document;
        if (!documentElement || !body) {
          this.scrollLockState = null;
          return;
        }

        const { scrollY, docOverflow, bodyOverflow, bodyPosition, bodyTop, bodyWidth } = this.scrollLockState;

        documentElement.classList.remove('buyers-guide-modal-open');
        body.classList.remove('buyers-guide-modal-open');
        documentElement.style.overflow = docOverflow;
        body.style.overflow = bodyOverflow;
        body.style.position = bodyPosition;
        body.style.top = bodyTop;
        body.style.width = bodyWidth;
        this.scrollLockState = null;

        window.scrollTo(0, scrollY);
      }

      matchesFilter(filterValue, selectedKey) {
        if (!selectedKey) return true;
        const normalizedFilterValues = this.parseKeys(filterValue);
        if (normalizedFilterValues.length === 0) return true;
        return normalizedFilterValues.includes(selectedKey);
      }

      shouldSkipPlugStep() {
        if (!this.state.productType) return false;
        if (this.skipPlugTypeKeys.includes(this.state.productType)) return true;

        const selectedProductType = this.productTypeOptions.find(
          (button) => this.normalizeKey(button.dataset.guideKey) === this.state.productType
        );
        const selectedLabel = String(selectedProductType?.dataset.guideLabel || '').toLowerCase();
        return this.skipPlugTypeLabels.some((keyword) => selectedLabel.includes(keyword));
      }

      isStepUnlocked(step) {
        if (step <= 1) return true;
        if (step === 2) return Boolean(this.state.vehicle);
        if (step === 3) return Boolean(this.state.vehicle && this.state.productType && !this.shouldSkipPlugStep());
        if (step === 4) {
          if (!this.state.vehicle || !this.state.productType) return false;
          return this.shouldSkipPlugStep() || Boolean(this.state.plugType);
        }
        return false;
      }

      onClick(event) {
        const variantOptionButton = event.target.closest('[data-guide-variant-option]');
        if (variantOptionButton) {
          this.selectVariantOption(variantOptionButton);
          return;
        }

        const openSpecsButton = event.target.closest('[data-guide-open-specs]');
        if (openSpecsButton) {
          this.openSpecsModal(openSpecsButton);
          return;
        }

        const closeSpecsButton = event.target.closest('[data-guide-spec-close]');
        if (closeSpecsButton) {
          this.closeSpecsModal();
          return;
        }

        const specAddCartButton = event.target.closest('[data-guide-spec-add-cart]');
        if (specAddCartButton) {
          this.triggerSpecAddToCart();
          return;
        }

        const productCard = event.target.closest('[data-guide-card-url]');
        if (productCard) {
          const interactiveTarget = event.target.closest(
            'a, button, input, select, textarea, summary, label, [role="button"], [data-ignore-card-link]'
          );
          if (interactiveTarget && productCard.contains(interactiveTarget)) return;

          const productUrl = productCard.dataset.guideCardUrl;
          if (productUrl) {
            window.location.href = productUrl;
            return;
          }
        }

        const selectButton = event.target.closest('[data-guide-select]');
        if (selectButton) {
          this.selectOption(selectButton);
          return;
        }

        const backButton = event.target.closest('[data-guide-back]');
        if (backButton) {
          let targetStep = Number(backButton.dataset.guideBack);
          if (targetStep === 3 && this.shouldSkipPlugStep()) {
            targetStep = 2;
          }
          if (!Number.isNaN(targetStep)) {
            this.state.step = targetStep;
            this.sync();
          }
          return;
        }

        const stepButton = event.target.closest('[data-guide-step-button]');
        if (stepButton) {
          const targetStep = Number(stepButton.dataset.guideStepButton);
          if (!Number.isNaN(targetStep) && this.isStepUnlocked(targetStep)) {
            this.state.step = targetStep;
            this.sync();
          }
          return;
        }

        const navButton = event.target.closest('[data-guide-nav-step]');
        if (navButton) {
          const targetStep = Number(navButton.dataset.guideNavStep);
          if (!Number.isNaN(targetStep) && this.isStepUnlocked(targetStep)) {
            this.state.step = targetStep;
            this.sync();
          }
        }
      }

      openSpecsModal(trigger) {
        if (!this.specModal) return;

        const productCard = trigger.closest('.buyers-guide__final-card');
        const cardImage = productCard?.querySelector('.buyers-guide__final-image');
        const cardTitleElement = productCard?.querySelector('.buyers-guide__final-title');
        const cardPrimaryAction = productCard?.querySelector('[data-guide-submit-form]');
        const cardProductForm = productCard?.querySelector('form[id^="BuyersGuideProductForm-"]');

        const nextValues = {
          connectorType: trigger.dataset.guideSpecConnectorType || '',
          acInputPlug: trigger.dataset.guideSpecAcInputPlug || '',
          operatingFrequency: trigger.dataset.guideSpecOperatingFrequency || '',
          operatingVoltage: trigger.dataset.guideSpecOperatingVoltage || '',
          installationMethod: trigger.dataset.guideSpecInstallationMethod || '',
          operatingCurrent: trigger.dataset.guideSpecOperatingCurrent || '',
          outputPower: trigger.dataset.guideSpecOutputPower || ''
        };

        if (this.specProductTitle) {
          this.specProductTitle.textContent =
            cardTitleElement?.textContent?.trim() || this.specProductDefaults.title || this.specProductTitle.textContent;
        }
        if (this.specProductImage) {
          const nextImageSrc = cardImage?.currentSrc || cardImage?.getAttribute('src') || this.specProductDefaults.imageSrc;
          const nextImageAlt =
            cardImage?.getAttribute('alt') ||
            cardTitleElement?.textContent?.trim() ||
            this.specProductDefaults.imageAlt;

          if (nextImageSrc) this.specProductImage.setAttribute('src', nextImageSrc);
          this.specProductImage.setAttribute('alt', nextImageAlt);
        }

        this.specActiveFormId = cardPrimaryAction?.dataset.guideSubmitForm || cardProductForm?.id || '';
        if (this.specAddCartButton) {
          const sourceLabel = 'Add to Cart';
          const modalLabel = this.specAddCartButton.querySelector('.btn-text > span');
          if (modalLabel) {
            modalLabel.textContent = sourceLabel;
          } else {
            this.specAddCartButton.textContent = sourceLabel;
          }
          const hasForm = Boolean(this.specActiveFormId);
          this.specAddCartButton.toggleAttribute('disabled', !hasForm);
          this.specAddCartButton.setAttribute('aria-disabled', hasForm ? 'false' : 'true');
        }

        let visiblePrimarySpecCount = 0;
        Object.keys(this.specTargets).forEach((key) => {
          const target = this.specTargets[key];
          if (!target) return;
          const value = String(nextValues[key] || '').trim();
          target.textContent = value;
          const row = target.closest('[data-guide-spec-item]');
          if (row) {
            const isHidden = value.length === 0;
            row.hidden = isHidden;
            if (!isHidden) visiblePrimarySpecCount += 1;
          }
        });

        if (this.specFallbackItems?.length) {
          this.specFallbackItems.forEach((item) => {
            item.hidden = true;
            const labelNode = item.querySelector('[data-guide-spec-fallback-label]');
            const valueNode = item.querySelector('[data-guide-spec-fallback-value]');
            if (labelNode) labelNode.textContent = '';
            if (valueNode) valueNode.textContent = '';
          });

          if (visiblePrimarySpecCount === 0) {
            const fallbackEntries = [];
            for (let index = 1; index <= 5; index += 1) {
              const label = String(trigger.dataset[`guideSpecFallback${index}Label`] || '').trim();
              const value = String(trigger.dataset[`guideSpecFallback${index}Value`] || '').trim();
              if (!label || !value) continue;
              fallbackEntries.push({ label, value });
            }

            this.specFallbackItems.forEach((item, index) => {
              const entry = fallbackEntries[index];
              if (!entry) {
                item.hidden = true;
                return;
              }
              const labelNode = item.querySelector('[data-guide-spec-fallback-label]');
              const valueNode = item.querySelector('[data-guide-spec-fallback-value]');
              if (labelNode) labelNode.textContent = entry.label;
              if (valueNode) valueNode.textContent = entry.value;
              item.hidden = false;
            });
          }
        }

        this.specModal.hidden = false;
        this.lockPageScroll();
        window.requestAnimationFrame(() => {
          this.specModal.classList.add('is-visible');
        });
      }

      submitGuideForm(formId) {
        if (!formId) return;

        const escapeAttr = (value) => {
          if (window.CSS?.escape) return window.CSS.escape(value);
          return String(value).replace(/["\\]/g, '\\$&');
        };
        const proxySelector = `[data-guide-submit-form="${escapeAttr(formId)}"]`;
        const proxySubmit = this.querySelector(proxySelector);
        if (proxySubmit && !proxySubmit.disabled && !proxySubmit.hasAttribute('aria-disabled')) {
          proxySubmit.click();
          return;
        }

        const selector = window.CSS?.escape ? `#${window.CSS.escape(formId)}` : `#${formId}`;
        const form = this.querySelector(selector);
        if (!form) return;

        const innerSubmit = form.querySelector('[type="submit"]');
        if (innerSubmit?.disabled || innerSubmit?.hasAttribute('aria-disabled')) return;

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(innerSubmit || undefined);
          return;
        }

        if (innerSubmit) {
          innerSubmit.click();
          return;
        }

        form.submit();
      }

      closeSpecsModal() {
        if (!this.specModal || this.specModal.hidden) return;
        this.specModal.classList.remove('is-visible');
        this.specModal.hidden = true;
        this.specActiveFormId = '';
        this.specAddPending = false;
        if (this.specAddCartButton) {
          this.specAddCartButton.removeAttribute('aria-busy');
          this.specAddCartButton.removeAttribute('aria-disabled');
        }
        this.unlockPageScroll();
      }

      triggerSpecAddToCart() {
        if (!this.specActiveFormId) return;
        this.specAddPending = true;
        if (this.specAddCartButton) {
          this.specAddCartButton.setAttribute('aria-busy', 'true');
          this.specAddCartButton.setAttribute('aria-disabled', 'true');
        }
        this.submitGuideForm(this.specActiveFormId);
      }

      handleAjaxProductAdded() {
        if (!this.specAddPending) return;
        this.closeSpecsModal();
        const cartDrawer = document.querySelector('cart-drawer');
        cartDrawer?.show(this.specAddCartButton || this);
      }

      handleAjaxProductError() {
        if (!this.specAddPending) return;
        this.specAddPending = false;
        if (this.specAddCartButton) {
          this.specAddCartButton.removeAttribute('aria-busy');
          this.specAddCartButton.removeAttribute('aria-disabled');
        }
      }

      handleDocumentKeydown(event) {
        if (event.key !== 'Escape') return;
        this.closeSpecsModal();
      }

      selectOption(button) {
        const type = button.dataset.guideSelect;
        const key = this.normalizeKey(button.dataset.guideKey);
        if (!type || !key) return;

        if (type === 'vehicle') {
          this.state.vehicle = key;
          this.state.productType = '';
          this.state.plugType = '';
          this.state.step = 2;
        } else if (type === 'product_type') {
          this.state.productType = key;
          this.state.plugType = '';
          this.state.step = this.shouldSkipPlugStep() ? 4 : 3;
        } else if (type === 'plug_type') {
          this.state.plugType = key;
          this.state.step = 4;
        }

        this.sync();
      }

      updateStepButtons() {
        const skipPlugStep = this.shouldSkipPlugStep();

        this.stepButtons.forEach((button) => {
          const step = Number(button.dataset.guideStepButton);
          let unlocked = this.isStepUnlocked(step);
          let isCurrent = this.state.step === step;
          let isComplete = this.state.step > step && unlocked;

          if (skipPlugStep && step === 3) {
            unlocked = false;
            isCurrent = false;
            isComplete = this.state.step >= 4;
          }

          button.disabled = !unlocked;
          button.classList.toggle('is-current', isCurrent);
          button.classList.toggle('is-complete', isComplete);
          button.setAttribute('aria-current', isCurrent ? 'step' : 'false');
        });

        this.topNavButtons.forEach((button) => {
          const step = Number(button.dataset.guideNavStep);
          const isCurrent = this.state.step === step;
          const isComplete = this.state.step > step;
          const unlocked = this.isStepUnlocked(step);
          button.disabled = !unlocked;
          button.setAttribute('aria-current', isCurrent ? 'step' : 'false');
          button.classList.toggle('is-current', isCurrent);
          button.classList.toggle('is-complete', isComplete);
        });
      }

      updateProgress() {
        const currentStep = Math.min(Math.max(this.state.step, 1), this.totalSteps);

        if (this.progressCurrent) {
          this.progressCurrent.textContent = this.formatStepNumber(currentStep);
        }

        let progressValue = 0;
        if (this.progressTrack && this.stepButtons.length) {
          const currentStepButton = this.stepButtons.find(
            (button) => Number(button.dataset.guideStepButton) === currentStep
          );
          const marker = currentStepButton?.querySelector('.buyers-guide__step-icon') || currentStepButton;
          const trackRect = this.progressTrack.getBoundingClientRect();
          const markerRect = marker?.getBoundingClientRect();

          if (trackRect.width > 0 && markerRect) {
            const markerCenter = markerRect.left + markerRect.width / 2;
            progressValue = ((markerCenter - trackRect.left) / trackRect.width) * 100;
          }
        }

        if (!Number.isFinite(progressValue) || progressValue <= 0) {
          progressValue = (currentStep / Math.max(this.totalSteps, 1)) * 100;
        }

        if (currentStep === this.totalSteps) {
          progressValue = 100;
        }

        progressValue = Math.min(Math.max(progressValue, 0), 100);

        if (this.progressFill) {
          this.progressFill.style.width = `${progressValue}%`;
        }
      }

      updatePanels() {
        this.panels.forEach((panel) => {
          const panelStep = Number(panel.dataset.guidePanel);
          panel.hidden = panelStep !== this.state.step;
        });
      }

      updateSelectedState(buttons, selectedKey) {
        buttons.forEach((button) => {
          const isSelected = this.normalizeKey(button.dataset.guideKey) === selectedKey;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
      }

      filterOptions() {
        let visibleProductTypeCount = 0;
        this.productTypeOptions.forEach((button) => {
          const visible = this.matchesFilter(button.dataset.guideVehicleKeys, this.state.vehicle);
          button.hidden = !visible;
          if (visible) visibleProductTypeCount += 1;
        });

        const selectedProductTypeButton = this.productTypeOptions.find(
          (button) => this.normalizeKey(button.dataset.guideKey) === this.state.productType
        );
        if (this.state.productType && (!selectedProductTypeButton || selectedProductTypeButton.hidden)) {
          this.state.productType = '';
          this.state.plugType = '';
          if (this.state.step > 2) this.state.step = 2;
        }

        let visiblePlugTypeCount = 0;
        this.plugTypeOptions.forEach((button) => {
          const visible =
            this.matchesFilter(button.dataset.guideVehicleKeys, this.state.vehicle) &&
            this.matchesFilter(button.dataset.guideProductTypeKeys, this.state.productType);
          button.hidden = !visible;
          if (visible) visiblePlugTypeCount += 1;
        });

        const selectedPlugTypeButton = this.plugTypeOptions.find(
          (button) => this.normalizeKey(button.dataset.guideKey) === this.state.plugType
        );
        if (this.state.plugType && (!selectedPlugTypeButton || selectedPlugTypeButton.hidden)) {
          this.state.plugType = '';
          if (this.state.step > 3) this.state.step = 3;
        }

        if (this.shouldSkipPlugStep()) {
          this.state.plugType = '';
          if (this.state.step === 3) this.state.step = 4;
        }

        if (this.emptyOptionMessages[2]) {
          this.emptyOptionMessages[2].hidden = visibleProductTypeCount > 0;
        }
        if (this.emptyOptionMessages[3]) {
          this.emptyOptionMessages[3].hidden = this.shouldSkipPlugStep() || visiblePlugTypeCount > 0;
        }
      }

      filterProducts() {
        let visibleProductCount = 0;
        this.productItems.forEach((item) => {
          const visible =
            this.matchesFilter(item.dataset.guideVehicleKeys, this.state.vehicle) &&
            this.matchesFilter(item.dataset.guideProductTypeKeys, this.state.productType) &&
            this.matchesFilter(item.dataset.guidePlugTypeKeys, this.state.plugType);

          item.hidden = !visible;
          if (visible) visibleProductCount += 1;
        });

        if (this.realProductsContainer) {
          this.realProductsContainer.classList.toggle('buyers-guide__products--single', visibleProductCount === 1);
        }

        const showDemoProducts = visibleProductCount === 0 && Boolean(this.demoProducts);
        if (this.demoProducts) {
          this.demoProducts.hidden = !showDemoProducts;
          const demoProductCount = this.demoProducts.querySelectorAll('.buyers-guide__final-card').length;
          this.demoProducts.classList.toggle('buyers-guide__products--single', demoProductCount === 1);
        }

        if (this.emptyProductsMessage) {
          this.emptyProductsMessage.hidden = visibleProductCount > 0 || showDemoProducts;
        }
      }

      updateSummary() {
        const selectedVehicle = this.vehicleOptions.find(
          (button) => this.normalizeKey(button.dataset.guideKey) === this.state.vehicle
        );
        const selectedProductType = this.productTypeOptions.find(
          (button) => this.normalizeKey(button.dataset.guideKey) === this.state.productType
        );
        const selectedPlugType = this.plugTypeOptions.find(
          (button) => this.normalizeKey(button.dataset.guideKey) === this.state.plugType
        );

        if (this.summaryTargets.vehicle) {
          this.summaryTargets.vehicle.textContent = selectedVehicle?.dataset.guideLabel || '-';
        }
        if (this.summaryTargets.productType) {
          this.summaryTargets.productType.textContent = selectedProductType?.dataset.guideLabel || '-';
        }
        if (this.summaryTargets.plugType) {
          this.summaryTargets.plugType.textContent = selectedPlugType?.dataset.guideLabel || '-';
        }
      }

      sync() {
        this.filterOptions();

        this.updateSelectedState(this.vehicleOptions, this.state.vehicle);
        this.updateSelectedState(this.productTypeOptions, this.state.productType);
        this.updateSelectedState(this.plugTypeOptions, this.state.plugType);

        this.updateStepButtons();
        this.updateProgress();
        this.updatePanels();
        this.filterProducts();
        this.updateSummary();
      }
    }
  );
}
